import React, { useState, useEffect, useCallback } from "react";
import cx from "classnames";
import { useParams } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import { SelectPagination } from "@foris/avocado-suite";
import { campusSearch } from "@services/collections/campus/graphql/campusSearch.query";
import { Campus } from "@models/ISchema";
import { PathParams } from "@edhtypes/general";
import { ISelectorReturn } from "./ISelectors";
import css from "./selectors.module.scss";

interface CampusSelectProps extends Omit<ISelectorReturn, "label"> {
  defaultCampus?: Campus;
  isDisabled?: boolean;
  label?: string;
}

const getCampusOption = (campus: Campus) => {
  if (!campus?.code || !campus?.id) {
    return {
      label: "",
      value: "",
    };
  }

  const campusName = campus?.name ? ` - ${campus?.name}` : "";

  return {
    label: `${campus?.code}${campusName}`,
    value: campus?.id,
  };
};

const CampusSelect = (props: CampusSelectProps) => {
  const client = useApolloClient();
  const { onCallback, className, placeholder, defaultCampus, isDisabled = false, label } = props;
  const { scenario }: PathParams = useParams();
  const [valueCampus, setValueCampus] = useState(
    defaultCampus ? getCampusOption(defaultCampus) : null,
  );
  const [page, setPage] = useState(0);
  const [prevSearchTerm, setPrevSearchTerm] = useState("");

  const requestItems = useCallback(
    async (searchTerm = "", page = 1) => {
      const size = 20;

      try {
        const variables = {
          query: campusSearch,
          variables: {
            scenarioId: scenario,
            originId: origin,
            filter: {
              pagination: { page, size, searchTerm },
            },
          },
        };

        const { data } = await client.query(variables);
        const dataQuery = data?.data;
        const pageInfo = dataQuery?.groupsManagerCampuses?.pageInfo;
        const campuses = dataQuery?.groupsManagerCampuses?.items;

        const options = (campuses ?? []).map((campus: Campus) => ({
          id: campus?.id,
          value: campus?.id,
          label: `${campus?.code}, ${campus?.name}`,
          self: campus,
        }));

        return { pageInfo, options };
      } catch (error) {
        console.error(error);
        return {};
      }
    },
    [client],
  );

  const loadOptions = async (newSearchTerm: string) => {
    const newSearchPage = prevSearchTerm === newSearchTerm ? page + 1 : 1;

    setPrevSearchTerm(newSearchTerm);
    setPage(newSearchPage);

    const { pageInfo, options } = await requestItems(newSearchTerm, newSearchPage);

    return {
      options: options,
      hasMore: pageInfo?.hasNextPage,
      additional: { page },
    };
  };

  useEffect(() => {
    if (defaultCampus?.id) {
      onCallback?.(valueCampus);
    }
  }, []);

  return (
    <div className={cx(css.selectors, className)}>
      <SelectPagination
        label={label}
        loadOptions={loadOptions}
        value={valueCampus}
        placeholder={placeholder}
        isClearable
        disabled={isDisabled}
        onChange={value => {
          if (value) {
            setValueCampus(value);
            onCallback?.(value);
          } else {
            setValueCampus(null);
            onCallback?.(value);
          }
        }}
      />
    </div>
  );
};

export default CampusSelect;
